import type { ReactNode } from 'react';

import classNames from 'classnames';

import { selectCuttingRoomBaseUrl } from '../../ducks/cutting_room/base_url';
import { useAppSelector } from '../../store/react_redux_typed';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import { useCuttingRoomIframe } from './use_cutting_room_iframe';

import styles from './cutting_room_iframe.module.css';

export type CuttingRoomIframeProps = {
  view: 'player' | 'project';
  projectId?: string;
  hide?: string[];
  className?: string;
  /**
   * Shown in place of the embed, which stays loaded and signing in but out of
   * view. The loading bar still shows until sign-in settles, and a failed
   * sign-in reveals the embed anyway so CuttingRoom's own screen says what went
   * wrong.
   */
  placeholder?: ReactNode;
  /** Keeps the loading bar up past sign-in, for work still in progress. */
  isLoading?: boolean;
};

export function CuttingRoomIframe(props: CuttingRoomIframeProps) {
  const baseUrl = useAppSelector(selectCuttingRoomBaseUrl);

  // An empty src would resolve to the current document, nesting the app in
  // itself.
  if (!baseUrl) {
    return null;
  }

  return <CuttingRoomIframeContent {...props} baseUrl={baseUrl} />;
}

function CuttingRoomIframeContent({
  baseUrl,
  view,
  projectId,
  hide,
  className,
  placeholder,
  isLoading = false,
}: CuttingRoomIframeProps & { baseUrl: string }) {
  const { iframeProps, authorizationStatus } = useCuttingRoomIframe({
    baseUrl,
    view,
    projectId,
    hide,
  });

  // The iframe shows its own login form until CuttingRoom accepts our token, so
  // it stays behind the loading bar until sign-in reaches a verdict either way.
  const isSignInPending = authorizationStatus === 'pending';
  const isEmbedShown = !placeholder || authorizationStatus === 'failed';

  return (
    <>
      <div
        className={classNames(
          styles.root,
          { [styles.collapsed]: !isEmbedShown },
          className,
        )}
      >
        {(isSignInPending || isLoading) && (
          <LoadingBar className={styles.loading_bar} />
        )}
        <iframe
          {...iframeProps}
          className={classNames(styles.iframe, {
            [styles.hidden]: isSignInPending || !isEmbedShown,
          })}
        ></iframe>
      </div>
      {!isEmbedShown && placeholder}
    </>
  );
}
